// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState } from 'react'
import { Info, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface InfoButtonProps {
  title:     string
  content:   React.ReactNode
  className?: string
}

export function InfoButton({ title, content, className }: InfoButtonProps) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-full',
          'bg-sage-100 text-sage-700 hover:bg-sage-200 active:bg-sage-300',
          'transition-colors cursor-pointer flex-shrink-0',
          className,
        )}
        aria-label={`Meer informatie over ${title}`}
        title={`Meer informatie over ${title}`}
      >
        <Info className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.45)' }}
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-sand-200">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-sage-600 flex-shrink-0" />
                <h3 className="text-base font-semibold text-ink">{title}</h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-ink-4 hover:bg-sand-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {/* Content */}
            <div className="px-5 py-4 text-sm text-ink-3 leading-relaxed space-y-3 max-h-[60vh] overflow-y-auto">
              {content}
            </div>
            {/* Close button */}
            <div className="px-5 pb-5">
              <button
                onClick={() => setOpen(false)}
                className="w-full rounded-xl bg-moss text-white py-3 text-sm font-semibold hover:bg-moss-light transition-colors"
              >
                Begrepen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
